import React, { useEffect, useState } from 'react';
import { useAuth, API_URL, Currency, DateBR } from '../contexts/AuthContext';
import { TrendingUp, DollarSign, Package, AlertCircle, Clock, Banknote, ShoppingCart, Users } from 'lucide-react';

const StatCard: React.FC<{ title: string; value: React.ReactNode; icon: React.ElementType; color: string; trend?: string }> = ({ title, value, icon, color, trend }) => (
  <div className="card">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{title}</p>
        <div className="text-2xl font-bold">{value}</div>
        {trend && <p className={`text-xs mt-1 ${trend.startsWith('-') ? 'text-red-600' : 'text-green-600'}`}>{trend}</p>}
      </div>
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center text-white`}>
        <icon size={20} />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [salesDaily, setSalesDaily] = useState<any[]>([]);
  const [expDaily, setExpDaily] = useState<any[]>([]);
  const [top, setTop] = useState<any[]>([]);
  const [profit, setProfit] = useState<any[]>([]);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      fetch(`${API_URL}/reports/summary`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/reports/sales-daily?days=14`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/reports/expenses-daily?days=14`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/reports/top-products?limit=6`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/reports/profit-by-product`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ]).then(([s, sd, ed, t, p]) => {
      setStats(s); setSalesDaily(sd); setExpDaily(ed); setTop(t); setProfit(p);
    }).catch(() => {});
  }, [token]);

  if (!stats) return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
      <div className="card p-8 text-center text-gray-500">Carregando painel...</div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Visão geral do comércio de bebidas</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Faturamento (período)" value={Currency(stats.faturamento)} icon={ShoppingCart} color="bg-blue-500" trend={`+${((stats.margem_percentual||0)*1).toFixed(0)}% margem`} />
        <StatCard title="Lucro bruto" value={Currency(stats.lucro_bruto)} icon={TrendingUp} color="bg-green-500" />
        <StatCard title="Lucro líquido est." value={Currency(stats.lucro_liquido)} icon={DollarSign} color="bg-indigo-500" />
        <StatCard title="Caixa / a receber" value={Currency(stats.a_receber)} icon={Banknote} color="bg-amber-500" />
        <StatCard title="Estoque (custo)" value={Currency(stats.estoque_custo)} icon={Package} color="bg-purple-500" />
        <StatCard title="A pagar" value={Currency(stats.a_pagar)} icon={AlertCircle} color="bg-red-500" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Vendas vs Despesas (últimos 14 dias)</h2>
          <div className="h-64 w-full">Gráfico (canvas) — dados: {salesDaily.length} dias vendas, {expDaily.length} dias despesas</div>
        </div>
        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Lucro por produto</h2>
          <div className="space-y-2">
            {(profit||[]).slice(0,6).map((p:any) => (
              <div key={p.name} className="text-sm border-b pb-2 last:border-0">
                <div className="flex justify-between">{p.name} <span>{Currency(p.lucro_bruto || 0)}</span></div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Produtos mais vendidos</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b border-gray-200"><th className="pb-2 text-gray-500">Produto</th><th className="pb-2 text-gray-500 text-right">Qtd</th><th className="pb-2 text-gray-500 text-right">Faturamento</th></tr></thead>
            <tbody>
              {(top||[]).map((p:any) => (
                <tr key={p.name} className="border-b border-gray-100 last:border-0">
                  <td className="py-2">{p.name}</td>
                  <td className="py-2 text-right">{p.qtd_vendida}</td>
                  <td className="py-2 text-right">{Currency(p.faturamento)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
